import { location, SearchCity } from '@/utils/aMap'
import { movieCityListApi } from '@/api/movie'

const DEFAULT_LAT = 23.12463
const DEFAULT_LNG = 113.36199
const DEFAULT_CITY = '广州市'
const DEFAULT_PROVINCE = '广东省'

/**
 * 通过电影城市接口匹配 cityName，写入 state.locationInfo.city_id
 */
const fetchAndSetMovieCityId = async (state, cityName) => {
  if (!cityName) return
  try {
    const res = await movieCityListApi({ keyword: cityName })
    const cityList = res.data?.data?.cityList
    if (!Array.isArray(cityList) || !cityList.length) return

    const matched =
      cityList.find(c => {
        const name = c.name || c.city_name || ''
        return (
          name === cityName ||
          cityName.includes(name) ||
          name.includes(cityName)
        )
      }) || cityList[0]

    state.locationInfo.city_id = matched.id || matched.city_id || ''
  } catch (err) {
    console.error('获取电影城市ID失败:', err)
  }
}

// ---- 高德回调 promisify ----
const pGetCurrentPosition = loc =>
  new Promise(r => loc.getCurrentPosition((s, d) => r([s, d])))
const pGetCityInfo = loc =>
  new Promise(r => loc.getCityInfo((s, d) => r([s, d])))
const pGetAddress = (s, lnglat) =>
  new Promise(r => s.getAddress(lnglat, (st, d) => r([st, d])))
const pSearchLocation = (s, city) =>
  new Promise(r => s.getLocation(city, (st, d) => r([st, d])))

// ---- state 写入工具 ----
const setLatLng = (state, lng, lat) => {
  state.locationInfo.lng = lng
  state.locationInfo.lat = lat
}
const setCityInfo = (state, city, province) => {
  state.locationInfo.city = city || DEFAULT_CITY
  state.locationInfo.province = province || DEFAULT_PROVINCE
}
const applyDefault = state => {
  setLatLng(state, DEFAULT_LNG, DEFAULT_LAT)
  setCityInfo(state)
}

// 城市确定后：拉电影 city_id 并返回标准负载
const finalize = async (state, data, message) => {
  await fetchAndSetMovieCityId(state, state.locationInfo.city)
  return { status: true, data, message }
}
const fallback = (state, message) => {
  applyDefault(state)
  return finalize(state, {}, message)
}

// 有经纬度时按 location_type 分派
const resolveCityByType = async (state, result) => {
  const lng = result.position.lng || result.position[0]
  const lat = result.position.lat || result.position[1]
  setLatLng(state, lng, lat)

  const type = result.location_type

  if (type === 'ipcity') {
    setCityInfo(state, result.city, result.province)
    return finalize(state, result, '通过IP定位获取城市信息成功')
  }

  if (type === 'html5') {
    const searchCity = await SearchCity().catch(err => {
      console.error(err, '初始化获取城市失败')
      return null
    })
    if (!searchCity) {
      setCityInfo(state, result.city, result.province)
      return finalize(state, result, '获取定位成功，城市信息获取失败')
    }
    try {
      const [st, addr] = await pGetAddress(searchCity, [lng, lat])
      if (st === 'complete' && addr.info === 'OK') {
        const { city, province } = addr.regeocode.addressComponent
        setCityInfo(state, city, province)
        return finalize(state, addr, 'HTML5定位获取城市信息成功')
      }
      setCityInfo(state, result.city, result.province)
      return finalize(state, result, '获取定位成功，地址解析失败')
    } catch (err) {
      console.error('获取城市信息失败:', err)
      setCityInfo(state, result.city, result.province)
      return finalize(state, result, '获取定位成功，处理过程中出现异常')
    }
  }

  setCityInfo(state, result.city, result.province)
  return finalize(state, result, '获取定位成功')
}

const getLocation = async ({ state }) => {
  try {
    const loc = await location().catch(err => {
      console.error(err, '初始化定位失败')
      return null
    })
    if (!loc) return fallback(state, '使用默认位置')

    const [status, result] = await pGetCurrentPosition(loc)

    // 1. 定位失败 → 尝试 IP 城市
    if (status !== 'complete') {
      console.warn('定位失败，尝试通过IP获取位置')
      const [cStatus, cResult] = await pGetCityInfo(loc)
      if (cStatus !== 'complete') {
        console.warn('IP定位也失败，使用默认位置')
        return fallback(state, '定位失败，使用默认位置')
      }
      setLatLng(state, cResult.center?.[0] || '', cResult.center?.[1] || '')
      setCityInfo(state, cResult.city, cResult.province)
      return finalize(state, { ...cResult }, '通过IP获取位置信息成功')
    }

    // 2. 定位成功 + 经纬度齐全
    const hasLatLng =
      result.position &&
      (result.position.lat || result.position[1]) &&
      (result.position.lng || result.position[0])
    if (hasLatLng) return resolveCityByType(state, result)

    // 3. 无经纬度但有基础城市信息
    if (result.province) {
      setCityInfo(state, result.city, result.province)
      if (result.position && result.position.length >= 2) {
        setLatLng(state, result.position[0] || '', result.position[1] || '')
      }
      return finalize(state, result, '获取基础定位信息成功')
    }

    // 4. 信息不完整
    console.warn('定位信息不完整，使用默认位置')
    return fallback(state, '定位信息不完整，使用默认位置')
  } catch (err) {
    console.error('定位服务异常:', err)
    return fallback(state, '定位服务异常，使用默认位置')
  }
}

const setCity = async ({ state }, cityName) => {
  try {
    const searchCity = await SearchCity()
    const [status, result] = await pSearchLocation(searchCity, cityName)
    console.log('SearchCity结果:', status, result)

    if (status !== 'complete' || !result.geocodes.length) {
      return { status: false, message: '设置城市失败' }
    }

    const { location: loc, addressComponent } = result.geocodes[0]
    setLatLng(state, loc.lng, loc.lat)
    setCityInfo(state, addressComponent.city, addressComponent.province)
    await fetchAndSetMovieCityId(state, state.locationInfo.city)
    return { status: true, data: result, message: '定设置城市成功' }
  } catch (err) {
    console.error('SearchCity失败', err)
    return { status: false, message: 'SearchCity失败' }
  }
}

const state = {
  locationInfo: {
    accuracy: '',
    adcode: '',
    addr: '',
    city: '',
    district: '',
    lat: '',
    lng: '',
    module: '',
    nation: '',
    province: '',
    type: '',
    id: '',
    city_id: ''
  }
}

export default {
  namespaced: true,
  state,
  mutations: {},
  actions: { getLocation, setCity }
}
